import React, { FC, useEffect, useState } from 'react';
import styles from './styles.module.css';
import { BulkRequest, LastUpdate } from '..';
import { Check, RotateCcw, X as Close } from 'lucide-react';
import { Button } from 'antd';
import { undoBulkRequest } from 'appJS/api/CommandApi';
import Loader from '../../../../../../clients/sidebar/components/Loader';
import animationStyles from '../../../../../../clients/sidebar/views/CreateTask/create_task/overlay/animation.module.css';
import cx from 'classnames';

type Props = {
  taskCount: number;
  bulkActions: BulkRequest[];
  lastUpdateAction: BulkRequest | undefined;
  lastBulkUpdate: LastUpdate | null;
  updating: boolean;
  merging: boolean;
  moving: boolean;
  deleting: boolean;
  onUndoComplete: () => void;
  setLastBulkUpdate: (lastBulkUpdate: LastUpdate | null) => void;
  moved: boolean;
  deleted: boolean;
  merged: boolean;
};

const StatusBar: FC<Props> = ({
  taskCount,
  bulkActions,
  lastUpdateAction,
  lastBulkUpdate,
  updating,
  merging,
  moving,
  deleting,
  onUndoComplete,
  setLastBulkUpdate,
  moved,
  deleted,
  merged
}) => {
  const [undoing, setUndoing] = useState<number>(0);

  useEffect(() => {
    if (bulkActions) {
      const undoneRequest: BulkRequest | undefined = bulkActions.find(
        ({ relatedBulkRequestId }) =>
          relatedBulkRequestId === lastUpdateAction?.id
      );

      if (undoneRequest?.completedAt) {
        setUndoing(0);

        setTimeout(() => setLastBulkUpdate(null), 300);
        setTimeout(onUndoComplete, 500);
      }
    }
  }, [bulkActions]);

  const showStatusBar =
    lastUpdateAction || (lastBulkUpdate?.id && (merging || deleting || moving));

  if (!showStatusBar) return null;

  const handleUndoBulkAction = () => {
    if (!lastUpdateAction) return;

    setUndoing(lastUpdateAction.id);

    const { details } = lastUpdateAction;
    Object.assign(details, { action: 'update_task' });

    undoBulkRequest(Object.assign(lastUpdateAction, details));
  };

  const showLoader = updating || merging;

  const count =
    ((deleted || moved || merged) && taskCount) ||
    lastUpdateAction?.details?.[showLoader ? 'objects' : 'success']?.length ||
    lastBulkUpdate?.taskIds?.length ||
    0;

  const actionLabel = () => {
    let label = 'updated';
    if (showLoader) label = 'updating';
    if (deleting) label = 'deleting';
    if (merging) label = 'merging';
    if (moving) label = 'moving';
    if (deleted) label = 'deleted';
    if (moved) label = 'moved';
    if (merged) label = 'merged';
    return label;
  };

  return (
    <div className={styles.container}>
      {showLoader ? (
        <Loader
          useDarkStyles
          className={cx(animationStyles.loaderIcon, styles.loaderIcon)}
        />
      ) : (
        <span className={styles.checkContainer}>
          <Check className={styles.checkIcon} />
        </span>
      )}
      <span
        className={styles.latestBulkAction}
      >{`${count} Tasks ${actionLabel()}`}</span>
      {!showLoader && !moved && !deleted && !merged && lastUpdateAction && (
        <Button
          className={styles.undoButton}
          type="link"
          icon={<RotateCcw className={styles.rotateIcon} />}
          loading={!!undoing}
          onClick={handleUndoBulkAction}
        >
          Undo
        </Button>
      )}
      <div
        className={styles.closeContainer}
        onClick={() => setLastBulkUpdate(null)}
      >
        <Close className={styles.closeIcon} />
      </div>
    </div>
  );
};

export default StatusBar;
